import { z } from 'zod';

/**
 * Runtime configuration. Production serves /config.js which sets window.__NUCLEUS_CONFIG__
 * before the app bundle loads, so one immutable build is promoted across environments.
 * Local dev falls back to VITE_* variables from .env files.
 */
export const runtimeConfigSchema = z.object({
  apiBaseUrl: z.url(),
  env: z.enum(['development', 'staging', 'production']).default('development'),
  /** "mock" boots MSW in the browser; "live" hits apiBaseUrl. */
  apiMode: z.enum(['live', 'mock']).default('live'),
  /** Sentry-compatible DSN (GlitchTip). Empty disables monitoring. */
  monitoringDsn: z.string().default(''),
  /** Build identifier for monitoring releases and support screens. */
  release: z.string().default('dev'),
  /** Where the tenant-facing docs live; used by help links. */
  docsUrl: z.url().optional(),
  /** The tenant admin app; the platform console opens support sessions there. */
  adminAppUrl: z.url().optional(),
  /**
   * Guided tours: `enabled: false` hides every entry point (rollout kill switch); `welcome: false`
   * keeps tours but never shows the first sign-in prompt.
   */
  tours: z
    .object({ enabled: z.boolean().default(true), welcome: z.boolean().default(true) })
    .default({ enabled: true, welcome: true }),
});

export type RuntimeConfig = z.infer<typeof runtimeConfigSchema>;

declare global {
  interface Window {
    __NUCLEUS_CONFIG__?: Partial<RuntimeConfig>;
  }
}

let cached: RuntimeConfig | undefined;

/** Reads and validates config once. Throws at boot when misconfigured, never at runtime. */
export type RuntimeConfigInput = { [K in keyof RuntimeConfig]?: RuntimeConfig[K] | undefined };

export function loadRuntimeConfig(envFallback: RuntimeConfigInput = {}): RuntimeConfig {
  if (cached) return cached;
  const injected = typeof window !== 'undefined' ? (window.__NUCLEUS_CONFIG__ ?? {}) : {};
  const result = runtimeConfigSchema.safeParse({
    ...stripUndefined(envFallback),
    ...stripUndefined(injected),
  });
  if (!result.success) {
    throw new Error(`Invalid runtime config: ${z.prettifyError(result.error)}`);
  }
  cached = { ...result.data, apiMode: apiModeOverride() ?? result.data.apiMode };
  return cached;
}

/** localStorage key that forces mock or live mode in this browser only. */
export const API_MODE_STORAGE_KEY = 'nucleus:apiMode';

// Lets the e2e runner and a developer flip modes without editing public/config.js.
function apiModeOverride(): RuntimeConfig['apiMode'] | undefined {
  if (typeof window === 'undefined') return undefined;
  try {
    const value = window.localStorage.getItem(API_MODE_STORAGE_KEY);
    return value === 'mock' || value === 'live' ? value : undefined;
  } catch {
    return undefined;
  }
}

export function getRuntimeConfig(): RuntimeConfig {
  if (!cached) throw new Error('Runtime config not loaded. Call loadRuntimeConfig() at boot.');
  return cached;
}

/** Test helper. */
/** localStorage key that turns the welcome prompt on in mock mode (off by default there, e2e). */
export const TOUR_WELCOME_STORAGE_KEY = 'nucleus:tours:welcome';

/** The welcome prompt shows when config allows it; in mock mode only when opted in. */
export function welcomePromptAllowed(config: RuntimeConfig): boolean {
  if (!config.tours.enabled || !config.tours.welcome) return false;
  if (config.apiMode !== 'mock') return true;
  try {
    return window.localStorage.getItem(TOUR_WELCOME_STORAGE_KEY) === 'on';
  } catch {
    return false;
  }
}

export function resetRuntimeConfig(): void {
  cached = undefined;
}

function stripUndefined(obj: object): Record<string, unknown> {
  return Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined && v !== ''));
}
